import { ApiClientError, NetworkError } from '@recipes/api-client';
import type { HintMarker, Product, PurchaseHint, Store } from '@recipes/contracts';
import {
  canonicalGtin,
  findLabelConflicts,
  nearestStore,
  normalizeIngredientName,
  resolveHints,
  type LabelConflict,
} from '@recipes/domain';
import { api } from '@/lib/api';
import { offlineDb, type CatalogProductRow } from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';
import {
  dietarySettings,
  notifyCatalog,
  toHintRow,
  toIngredientRow,
  toProductRow,
} from './device-copy';

/**
 * Barcode lookup for the scanner (docs/finished/ingredient-catalog-spec.md 5.1, 6.3, 6.9): the device
 * copy answers first – known products need no network at all – then the API (household →
 * shared cache → provider). Offline, an unknown code goes straight to the capture form.
 */

export type ScanOutcome =
  | {
      status: 'found';
      product: Product;
      hints: PurchaseHint[];
      conflicts: LabelConflict[];
      /** Where the answer came from; DEVICE means no request was needed. */
      origin: 'DEVICE' | 'HOUSEHOLD' | 'CACHE' | 'PROVIDER';
      pending: boolean;
    }
  | { status: 'unknown'; barcode: string; offline: boolean }
  | { status: 'invalid' };

/** Own rows win over global ones, captured-here rows over everything (they are the newest). */
function rank(row: CatalogProductRow): number {
  if (row.origin === 'local') return 0;
  return row.product.scope === 'GLOBAL' ? 2 : 1;
}

export async function hintsForProduct(product: {
  id: string;
  ingredientId: string | null;
}): Promise<PurchaseHint[]> {
  const rows = await offlineDb.catalogHints
    .where('productId')
    .equals(product.id)
    .or('ingredientId')
    .equals(product.ingredientId ?? '-')
    .toArray();
  return rows.map((r) => r.hint).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

async function fromDevice(barcode: string): Promise<ScanOutcome | null> {
  const rows = await offlineDb.catalogProducts.where('barcode').equals(barcode).toArray();
  const row = rows.sort((a, b) => rank(a) - rank(b))[0];
  if (!row) return null;
  const product = row.product;
  return {
    status: 'found',
    product,
    hints: await hintsForProduct({ id: product.id, ingredientId: row.ingredientId }),
    conflicts: findLabelConflicts(product, await dietarySettings()),
    origin: 'DEVICE',
    pending: row.origin === 'local',
  };
}

export async function lookupBarcode(raw: string, householdId?: string): Promise<ScanOutcome> {
  const barcode = canonicalGtin(raw);
  if (!barcode) return { status: 'invalid' };

  const local = await fromDevice(barcode);
  // A household product on the device is authoritative; no need to ask anyone.
  if (local?.status === 'found' && local.product.scope !== 'GLOBAL') return local;

  const platform = await getPlatform();
  if (!platform.isOnline()) return local ?? { status: 'unknown', barcode, offline: true };

  try {
    const response = await api.products.byBarcode(barcode, householdId);
    await offlineDb.transaction(
      'rw',
      [offlineDb.catalogProducts, offlineDb.catalogHints, offlineDb.catalogIngredients],
      async () => {
        const origin = response.product.scope === 'GLOBAL' ? 'lookup' : 'sync';
        await offlineDb.catalogProducts.put(toProductRow(response.product, origin));
        await offlineDb.catalogHints.bulkPut(response.hints.map((h) => toHintRow(h)));
      },
    );
    notifyCatalog();
    return {
      status: 'found',
      product: response.product,
      hints: response.hints,
      conflicts: response.conflicts,
      origin: response.origin,
      pending: false,
    };
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 404)
      return { status: 'unknown', barcode, offline: false };
    if (error instanceof ApiClientError && error.status === 400) return { status: 'invalid' };
    // Bad reception, timeouts, a session that is still refreshing: fall back to the device.
    if (error instanceof NetworkError || error instanceof ApiClientError)
      return local ?? { status: 'unknown', barcode, offline: true };
    throw error;
  }
}

/** Best local ingredient for a written name (household first is decided by the server copy). */
export async function resolveIngredientLocally(name: string) {
  const key = normalizeIngredientName(name);
  if (!key) return null;
  const candidates = [key, key.replace(/n$/, ''), key.replace(/en$/, '')];
  for (const candidate of candidates) {
    const rows = await offlineDb.catalogIngredients
      .where('normalizedName')
      .equals(candidate)
      .or('aliasKeys')
      .equals(candidate)
      .toArray();
    const best = rows.sort(
      (a, b) => Number(a.ingredient.scope === 'GLOBAL') - Number(b.ingredient.scope === 'GLOBAL'),
    )[0];
    if (best) return best.ingredient;
  }
  return null;
}

export { toIngredientRow };

export interface ItemHints {
  marker: HintMarker;
  applicable: PurchaseHint[];
  elsewhere: PurchaseHint[];
}

/** Hints for one shopping-list item from the device copy (works offline). */
export async function hintsForItem(
  item: { ingredientId?: string | null; productId?: string | null },
  store: Store | null,
): Promise<ItemHints> {
  const rows = await offlineDb.catalogHints
    .where('ingredientId')
    .equals(item.ingredientId ?? '-')
    .or('productId')
    .equals(item.productId ?? '-')
    .toArray();
  return resolveHints(
    rows.map((r) => r.hint),
    {
      ingredientId: item.ingredientId ?? null,
      productId: item.productId ?? null,
      storeId: store?.id ?? null,
      storeParentId: store?.parentStoreId ?? null,
    },
  );
}

/** "You are at …": the nearest known branch within 150 m, computed on the device. */
export async function proposeStore(position: { lat: number; lng: number }): Promise<Store | null> {
  const stores = (await offlineDb.catalogStores.toArray())
    .map((r) => r.store)
    .filter((s): s is Store & { lat: number; lng: number } => s.lat !== null && s.lng !== null);
  return nearestStore(position, stores)?.store ?? null;
}

/**
 * Ingredients whose name or alias starts with – or, failing that, contains – what was typed.
 * From the device copy, so suggestions work in the store without a connection. Own ingredients
 * come before global ones, names that start with the text before names that contain it.
 */
export async function searchIngredientsLocally(text: string, limit = 8) {
  const key = normalizeIngredientName(text);
  if (!key) return [];
  const starts = await offlineDb.catalogIngredients
    .where('normalizedName')
    .startsWith(key)
    .or('aliasKeys')
    .startsWith(key)
    .limit(limit * 4)
    .toArray();
  const seen = new Set(starts.map((row) => row.id));
  const contains =
    starts.length >= limit || key.length < 3
      ? []
      : await offlineDb.catalogIngredients
          .filter(
            (row) =>
              !seen.has(row.id) &&
              (row.normalizedName.includes(key) || row.aliasKeys.some((a) => a.includes(key))),
          )
          .limit(limit * 2)
          .toArray();
  const own = (row: (typeof starts)[number]) => Number(row.ingredient.scope === 'GLOBAL');
  const byRank = (a: (typeof starts)[number], b: (typeof starts)[number]) =>
    own(a) - own(b) ||
    a.normalizedName.length - b.normalizedName.length ||
    a.ingredient.name.localeCompare(b.ingredient.name);
  // One entry per name: an own ingredient shadows the global one of the same name.
  const names = new Set<string>();
  return [...starts.sort(byRank), ...contains.sort(byRank)]
    .filter((row) => !names.has(row.normalizedName) && names.add(row.normalizedName))
    .slice(0, limit)
    .map((row) => row.ingredient);
}
